import { useState } from 'react';
import { Link } from 'react-router-dom';
;
import { SEO } from '@/shared/SEO';
import { Sparkles, ArrowRight, Clock, BookOpen, Search } from 'lucide-react';
import { posts, getCategories } from '../data/posts';

export default function BlogPage() {
  const [selectedCategory, setSelectedCategory] = useState<string>('All');
  const [searchQuery, setSearchQuery] = useState('');

  const categories = ['All', ...getCategories()];

  const filtered = posts.filter((p) => {
    const matchesCategory = selectedCategory === 'All' || p.category === selectedCategory;
    const matchesSearch =
      searchQuery === '' ||
      p.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.category.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  // Featured = first post
  const featured = posts[0];
  const rest = filtered.filter((p) => p.slug !== featured.slug || selectedCategory !== 'All' || searchQuery !== '');

  return (
    <>
      <SEO
        title="Blog | Bloomneo — Practical Writing on Running Infrastructure"
        description="Honest writing on AI agents, managed cloud infrastructure, customer success, and how modern businesses run on private cloud. Read the Bloomneo blog."
        keywords="bloomneo blog, AI insights, business automation blog, managed cloud articles, customer success blog, software development insights"
      />

      {/* ━━━━━━━━━━━━━━━━ HERO ━━━━━━━━━━━━━━━━ */}
      <section className="relative">
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage: 'linear-gradient(rgba(232,168,48,1) 1px, transparent 1px), linear-gradient(90deg, rgba(232,168,48,1) 1px, transparent 1px)',
            backgroundSize: '60px 60px',
            maskImage: 'radial-gradient(ellipse at center top, black 40%, transparent 75%)',
          }}
        />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] rounded-full bg-primary/[0.05] blur-[150px] pointer-events-none" />

        <div className="relative pt-12 pb-12 md:pt-20 md:pb-16 text-center">
          <div className="bloom-rise">
            <div className="inline-flex items-center gap-2.5 bg-primary/10 border border-primary/20 rounded-full px-4 py-1.5 mb-6">
              <BookOpen className="h-3.5 w-3.5 text-primary" />
              <span className="text-[11px] font-semibold tracking-[0.12em] uppercase text-primary">
                Bloomneo Blog
              </span>
            </div>
          </div>

          <div className="bloom-rise">
            <h1 className="font-bold tracking-[-0.04em] leading-[0.92] mb-6" style={{ fontSize: 'clamp(2.5rem, 6.5vw, 5.5rem)' }}>
              Honest writing on<br />
              what actually{' '}
              <span className="relative inline-block">
                <span className="relative z-10 text-primary">works.</span>
                <span className="absolute bottom-1 left-0 w-full h-3 bg-primary/15 -z-0 -skew-x-6" />
              </span>
            </h1>
          </div>

          <div className="bloom-rise">
            <p className="text-base sm:text-lg md:text-xl text-muted-foreground leading-relaxed max-w-2xl mx-auto">
              No hype. Practical writing on running infrastructure — what it costs, what breaks, and who is responsible when it does.
            </p>
          </div>
        </div>
      </section>

      {/* ━━━━━━━━━━━━━━━━ FILTERS ━━━━━━━━━━━━━━━━ */}
      <section className="border-y border-border/40 bg-muted/30 sticky top-0 z-10 backdrop-blur">
        <div className="py-5">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            {/* Categories */}
            <div className="flex items-center gap-2 overflow-x-auto pb-1 md:pb-0 -mx-1 px-1">
              {categories.map((cat) => {
                const isActive = selectedCategory === cat;
                return (
                  <button
                    key={cat}
                    onClick={() => setSelectedCategory(cat)}
                    className={`flex-shrink-0 px-4 py-2 rounded-full text-xs font-semibold tracking-wide transition-all cursor-pointer ${
                      isActive
                        ? 'bg-primary text-primary-foreground shadow-md shadow-primary/20'
                        : 'bg-background border border-border/40 text-muted-foreground hover:text-foreground hover:border-primary/30'
                    }`}
                  >
                    {cat}
                  </button>
                );
              })}
            </div>

            {/* Search */}
            <div className="relative flex-shrink-0 w-full md:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search articles..."
                className="w-full pl-9 pr-4 py-2 rounded-full bg-background border border-border/40 text-sm placeholder:text-muted-foreground focus:outline-none focus:border-primary/50 transition-colors"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ━━━━━━━━━━━━━━━━ FEATURED ━━━━━━━━━━━━━━━━ */}
      {selectedCategory === 'All' && searchQuery === '' && (
        <section className="py-12 md:py-16">
          <div className="bloom-rise">
            <div className="mb-6 flex items-center gap-3">
              <div className="h-px w-8 bg-primary" />
              <span className="text-xs font-semibold tracking-[0.18em] uppercase text-primary">Featured</span>
            </div>
          </div>

          <div className="bloom-rise">
            <div>
              {/* A real link: an onClick navigation cannot be followed by a
                  crawler, by the build's pre-renderer, or by a reader opening it
                  in a new tab. Posts were reachable only from the sitemap. */}
              <Link
                to={`/blog/${featured.slug}`}
                className="group block w-full text-left bg-gradient-to-br from-primary/10 via-primary/5 to-primary/10 border border-primary/30 rounded-3xl p-8 md:p-12 transition-all hover:border-primary/50 cursor-pointer"
              >
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center">
                  <div className="lg:col-span-2">
                    <div className="flex items-center gap-3 mb-5">
                      <span className="text-[10px] font-semibold uppercase tracking-wider px-3 py-1 rounded-full bg-primary/20 text-primary border border-primary/30">
                        {featured.category}
                      </span>
                      <span className="text-xs text-muted-foreground flex items-center gap-1.5">
                        <Clock className="h-3 w-3" /> {featured.readTime}
                      </span>
                    </div>
                    <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight leading-tight mb-4 group-hover:text-primary transition-colors">
                      {featured.title}
                    </h2>
                    <p className="text-base text-muted-foreground leading-relaxed mb-6">
                      {featured.description}
                    </p>
                    <div className="flex items-center gap-2 text-primary font-semibold text-sm">
                      Read article
                      <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                  <div className="hidden lg:flex justify-end">
                    <div className="w-32 h-32 rounded-3xl bg-primary/10 border border-primary/20 flex items-center justify-center">
                      <Sparkles className="h-12 w-12 text-primary" />
                    </div>
                  </div>
                </div>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* ━━━━━━━━━━━━━━━━ POST GRID ━━━━━━━━━━━━━━━━ */}
      <section className="py-8 md:py-16">
        <div className="bloom-rise">
          <div className="mb-8 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-px w-8 bg-primary" />
              <span className="text-xs font-semibold tracking-[0.18em] uppercase text-primary">
                {selectedCategory === 'All' ? 'All Articles' : selectedCategory}
              </span>
            </div>
            <span className="text-xs text-muted-foreground">
              {filtered.length} {filtered.length === 1 ? 'article' : 'articles'}
            </span>
          </div>
        </div>

        {rest.length === 0 ? (
          <div className="bloom-rise">
            <div className="text-center py-20">
              <p className="text-muted-foreground">No articles found. Try a different category or search.</p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {rest.map((post, i) => (
              <div key={post.slug} className={`bloom-rise bloom-rise-${Math.min(i + 1, 4)}`}>
                <div>
                  <Link
                    to={`/blog/${post.slug}`}
                    className="group h-full w-full text-left bg-muted/30 border border-border/40 rounded-2xl p-7 hover:border-primary/30 transition-all cursor-pointer flex flex-col"
                  >
                    <div className="flex items-center gap-3 mb-4">
                      <span className="text-[10px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded-full bg-primary/10 text-primary border border-primary/20">
                        {post.category}
                      </span>
                      <span className="text-[10px] text-muted-foreground flex items-center gap-1">
                        <Clock className="h-3 w-3" /> {post.readTime}
                      </span>
                    </div>
                    <h3 className="text-lg md:text-xl font-bold tracking-tight leading-tight mb-3 group-hover:text-primary transition-colors">
                      {post.title}
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed mb-5 flex-1">
                      {post.description}
                    </p>
                    <div className="flex items-center justify-end pt-4 border-t border-border/40">
                      <div className="flex items-center gap-1 text-primary font-semibold text-xs">
                        Read more
                        <ArrowRight className="h-3 w-3 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </>
  );
}
